import { ETEX_CLASSIFICATIONS } from "./constants";
import { IActions, IState } from "./interfaces";
import React, { useEffect, useState } from "react";

import { ILanguageTranslations } from "./interfaces/languages";
import { useReactHookz } from "./state";

import styles from "./styles";

const ContactForm = ({ results }) => {
  const [ip, setIp] = useState("");

  const [statusMessage, setStatusMessage] = useState("");

  const [state]: [IState, IActions] = useReactHookz();
  const t = state.translations as ILanguageTranslations;

  const productQuantity = `${results.product}\n${results.total} ${t.resultLabels?.["total"]}`;

  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    etexClassification: "",
    country: "",
    zip: "",
    city: "",
    language: "",
    product_quantity: productQuantity,
    optInConsent: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value, type } = e.target as
      | HTMLInputElement
      | HTMLSelectElement;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  useEffect(() => {
    const loadIpAndHutk = async () => {
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        setIp(data.ip);
      } catch (error) {
        console.error("Error fetching IP:", error);
      }
    };

    loadIpAndHutk();
  }, []);

  const getHutk = () => {
    return (
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("hubspotutk="))
        ?.split("=")[1] || ""
    );
  };

  const getEnv = (): "local" | "dev" | "qa" | "uat" | "prod" => {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("dev"))
      return "dev";
    if (hostname.includes("uat")) return "uat";
    return "prod";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      fields: [
        { objectTypeId: "0-1", name: "email", value: form.email },
        { objectTypeId: "0-1", name: "firstname", value: form.firstName },
        { objectTypeId: "0-1", name: "lastname", value: form.lastName },
        { objectTypeId: "0-1", name: "company", value: form.company },
        {
          objectTypeId: "0-1",
          name: "etex_classification_l2__c",
          value: form.etexClassification,
        },
        { objectTypeId: "0-1", name: "country", value: state.country },
        { objectTypeId: "0-1", name: "zip", value: form.zip },
        { objectTypeId: "0-1", name: "city", value: form.city },
        {
          objectTypeId: "0-1",
          name: "etex_language__c",
          value: state.language.toUpperCase(),
        },
        {
          objectTypeId: "0-1",
          name: "ursa_opt_in_consent",
          value: form.optInConsent.toString(),
        },
        {
          objectTypeId: "0-1",
          name: "message",
          value: form.product_quantity,
        },
      ],
      context: {
        hutk: getHutk(),
        pageUri: "https://ursa.be/calculator",
        pageName: "Calculator",
        ipAddress: ip,
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: "I agree to allow URSA to store and process my personal data.",
        },
      },
    };

    setStatusMessage(t["submitting"]);

    try {
      console.log("Submitting form with payload:", payload);
      const response = await fetch("/api/purefloc/secure-hubspot/submit-form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatusMessage(t["submissionSuccess"]);
        setForm({
          email: "",
          firstName: "",
          lastName: "",
          company: "",
          etexClassification: "",
          country: state.country,
          zip: "",
          city: "",
          language: state.language.toUpperCase(),
          product_quantity: productQuantity,
          optInConsent: false,
        });
      } else {
        const err = await response.json();
        console.error(err);
        setStatusMessage(t["submissionError"]);
      }
    } catch (err) {
      console.error(err);
      setStatusMessage(t["networkError"]);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={styles.title}>{t["contactDetails"]}</h2>

      {/* EMAIL */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["email"]} <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="email"
          id="email"
          value={form.email}
          onChange={handleChange}
          required
          style={styles.inputDefault}
        />
      </div>

      {/* FIRST NAME */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["firstName"]} <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="text"
          id="firstName"
          value={form.firstName}
          onChange={handleChange}
          required
          style={styles.inputDefault}
        />
      </div>

      {/* LAST NAME */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["lastName"]} <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="text"
          id="lastName"
          value={form.lastName}
          onChange={handleChange}
          required
          style={styles.inputDefault}
        />
      </div>

      {/* COMPANY */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>{t["company"]}</label>
        <input
          type="text"
          id="company"
          value={form.company}
          onChange={handleChange}
          style={styles.inputDefault}
        />
      </div>

      {/* ETEX CLASSIFICATION */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["etexClassification"]} <span style={{ color: "red" }}>*</span>
        </label>
        <select
          id="etexClassification"
          value={form.etexClassification}
          onChange={handleChange}
          required
          style={styles.select}
        >
          <option value="">{t["selectPlaceholder"]}</option>
          {Object.entries(ETEX_CLASSIFICATIONS).map(([classification, id]) => (
            <option key={classification} value={id}>
              {classification}
            </option>
          ))}
        </select>
      </div>

      {/* ZIP */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["zip"]} <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="number"
          id="zip"
          value={form.zip}
          onChange={handleChange}
          required
          style={styles.inputDefault}
        />
      </div>

      {/* CITY */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>
          {t["city"]} <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="text"
          id="city"
          value={form.city}
          onChange={handleChange}
          required
          style={styles.inputDefault}
        />
      </div>

      {/* PRODUCT QUANTITY */}
      <div style={styles.inputField}>
        <label style={styles.inputLabel}>{t["productQuantity"]}</label>
        <textarea
          id="product_quantity"
          value={form.product_quantity}
          disabled
          rows={2}
          style={{
            ...styles.inputDefault,
            whiteSpace: "pre-wrap",
            resize: "none",
          }}
        />
      </div>

      {/* OPT-IN CONSENT */}
      <div style={styles.inputField}>
        <span></span>
        <label style={styles.inputLabel}>
          <input
            type="checkbox"
            id="optInConsent"
            checked={form.optInConsent}
            onChange={handleChange}
          />
          {t["optInConsentLabel"]}
        </label>
      </div>

      {/* OPT-IN CONSENT FORM */}
      <div style={styles.inputField}>
        <span></span>
        <label style={styles.inputLabel}>{t["optInConsentForm"]}</label>
      </div>

      {/* SUBMIT BUTTON */}
      <div style={styles.centerContent}>
        <button type="submit" style={styles.submitButton}>
          {t["submitButton"]}
        </button>
      </div>

      <p>{statusMessage}</p>
    </form>
  );
};

export default ContactForm;
